import { Link, useInRouterContext } from 'react-router-dom';
import { asSentence, t, taxJurisdictionRowLead, taxReportSentence, taxReviewSentences, taxRowLabel, taxZeroNote, type TaxPreviewAnswer } from '@bms2/shared';
import { Money } from '../Money';
import styles from './DocumentTotals.module.css';

/**
 * 🔒 The totals box a sales document draws under its lines (decision 0644):
 * Sub Total, Discount where a line carries one, one Tax row per jurisdiction
 * named with its rate and its base (decision 0738), and Total. It reads the tax preview's own answer and
 * computes nothing, so the box and the save agree by construction.
 *
 * 🔒 It is the Item Table's footer grown, never a second totals component: the
 * table's footer draws it, and the Credit Note, whose line block is not the
 * Item Table, draws the same one.
 *
 * 🔨 A preview that fails leaves the last figures standing with the failure
 * sentence beneath them. The save is never blocked by it, because the save
 * works the tax out again on its own.
 */
/**
 * The box's states on the Component Test Page: before the first answer,
 * answered, with a discount, a zero that says why, after a failed preview, a
 * row per jurisdiction (decision 0738), a ship-to headed for the report
 * (decision 0736), and a line charged at the standard rate over a Default
 * treatment, on the review report (decision 0904).
 */
export const documentTotalsDocumentedStates = ['waiting', 'answered', 'discounted', 'zero', 'failed', 'jurisdictions', 'reported', 'review'] as const;

export interface DocumentTotalsProps {
  /** The preview's last answer; null until the first one lands. */
  answer: TaxPreviewAnswer | null;
  /** The table's own net sum, drawn as the Sub Total until the first answer lands. */
  fallbackSubtotalCents: number;
  /** The server's refusal where the last preview failed; null where it did not. */
  failure: string | null;
  currencyCode?: string;
}

export function DocumentTotals({ answer, fallbackSubtotalCents, failure, currencyCode = 'USD' }: DocumentTotalsProps) {
  const subtotal = answer ? answer.subtotalCents : fallbackSubtotalCents;
  const total = answer ? answer.totalCents : fallbackSubtotalCents;
  const note = answer ? taxZeroNote(answer) : null;
  // 🔨 An answer from before the rows existed carries none, and draws its one Tax row as it always did.
  const rows = answer && Array.isArray(answer.rows) ? answer.rows : [];
  const reported = answer ? taxReportSentence({ stateCode: answer.stateCode, reportNote: answer.reportNote ?? null, hold: answer.hold ?? null }) : null;
  const inRouter = useInRouterContext();
  const holdRoute = answer?.hold ? `/locations/${answer.hold.locationId}` : null;
  // 🔨 An answer from before the reviews existed carries none, and draws no line for them.
  const reviewed = answer ? taxReviewSentences({ stateCode: answer.stateCode, reviews: Array.isArray(answer.reviews) ? answer.reviews : [] }) : [];
  return (
    <div className={styles.box} data-document-totals="true" data-totals-state={failure ? 'failed' : answer ? 'answered' : 'waiting'}>
      <div className={styles.row} data-totals-row="subtotal" data-cents={subtotal}>
        <span className={styles.label}>{t('document.print.subtotal')}</span>
        <span className={styles.value}><Money cents={subtotal} currencyCode={currencyCode} /></span>
      </div>
      {answer && answer.discountCents !== 0 ? (
        <div className={styles.row} data-totals-row="discount" data-cents={answer.discountCents}>
          <span className={styles.label}>{t('document.print.discount')}</span>
          <span className={styles.value}><Money cents={-answer.discountCents} currencyCode={currencyCode} /></span>
        </div>
      ) : null}
      {answer && rows.length > 0
        ? rows.map((row, index) => (
          // 🔒 One row per jurisdiction, never a column on each line: *Pennsylvania sales tax · 6% on $800.00* (decision 0738).
          <div key={`${row.level}-${row.jurisdictionId ?? row.stateCode}-${index}`} className={styles.row} data-totals-row="tax" data-tax-level={row.level} data-cents={row.taxCents} data-tax-state={row.stateCode}>
            <span className={styles.label}>
              {taxJurisdictionRowLead(row)} <Money cents={row.taxableBaseCents} currencyCode={currencyCode} />
            </span>
            <span className={styles.value}><Money cents={row.taxCents} currencyCode={currencyCode} /></span>
          </div>
        ))
        : answer ? (
          <div className={styles.row} data-totals-row="tax" data-cents={answer.taxCents} data-tax-state={answer.stateCode ?? ''}>
            <span className={styles.label}>{taxRowLabel(answer)}</span>
            <span className={styles.value}><Money cents={answer.taxCents} currencyCode={currencyCode} /></span>
          </div>
        ) : null}
      {note ? (
        // 🔒 A Tax row of nothing says why in one muted line, so a zero is never silent (decision 0644; piece 25).
        <p className={styles.note} data-totals-note={answer?.zeroReason ?? 'noStatewideTax'}>{note}</p>
      ) : null}
      {reported ? (
        // 🔒 A ship-to headed for the review report says why in the same muted line (decision 0736).
        <p className={styles.note} data-totals-note={answer?.reportNote ?? ''}>
          {reported}
          {/* 🔒 A held document points at the Location whose Verified by User releases it (decision 7460). */}
          {holdRoute ? <>{' '}{inRouter ? <Link to={holdRoute} data-totals-hold-location={answer?.hold?.locationId}>{t('tax.hold.openLocation')}</Link> : <a href={holdRoute} data-totals-hold-location={answer?.hold?.locationId}>{t('tax.hold.openLocation')}</a>}</> : null}
        </p>
      ) : null}
      {reviewed.map((sentence) => (
        // 🔒 A line charged at the standard rate over a Default treatment, or over no answer, says so in the same muted line, with its note (decision 0904).
        <p key={sentence} className={styles.note} data-totals-note="needs-review">{sentence}</p>
      ))}
      <div className={`${styles.row} ${styles.total}`} data-totals-row="total" data-cents={total}>
        <span className={styles.label}>{t('document.print.total')}</span>
        <span className={styles.value}><Money cents={total} currencyCode={currencyCode} /></span>
      </div>
      {failure ? (
        <p className={styles.failure} role="status" data-totals-failure="true">
          {t('tax.preview.failed')} {asSentence(failure)}
        </p>
      ) : null}
    </div>
  );
}
